import { useEffect, useState } from 'react';

export interface ViewportMetrics {
  /** Height of the region actually visible above the on-screen keyboard (px). */
  height: number;
  /** Offset of the visual viewport within the layout viewport (px). */
  offsetTop: number;
  /**
   * True when the on-screen keyboard is meaningfully occupying the viewport.
   * Used to drop the home-indicator safe-area padding while the keyboard is up
   * (the keyboard already covers that inset, so keeping it shows as a gap
   * between the input and the keyboard).
   */
  keyboardOpen: boolean;
}

/** Minimum layout-vs-visual delta (px) that counts as an open keyboard rather
 *  than browser-chrome rounding. Keyboards are ~250px+; chrome deltas are tiny. */
const KEYBOARD_THRESHOLD = 120;

/**
 * Tracks the visual viewport so the fullscreen widget can size itself to the
 * area *above* the software keyboard.
 *
 * `dvh`/`vh` only react to the browser's address bar, not the keyboard: on iOS
 * the keyboard never resizes the layout viewport, and Android's modern default
 * (`interactive-widget=resizes-visual`) behaves the same. The `visualViewport`
 * API is the one cross-platform signal that reports the shrunk region, so we
 * drive the container's height/top from it while fullscreen.
 *
 * Returns `null` when disabled or when the API is unavailable, so callers fall
 * back to their static `dvh` sizing.
 */
export function useVisualViewport(enabled: boolean): ViewportMetrics | null {
  const [metrics, setMetrics] = useState<ViewportMetrics | null>(null);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!enabled || !vv) {
      setMetrics(null);
      return;
    }
    const update = () =>
      setMetrics({
        height: vv.height,
        offsetTop: vv.offsetTop,
        keyboardOpen: window.innerHeight - vv.height > KEYBOARD_THRESHOLD,
      });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [enabled]);

  return metrics;
}
